/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  Input,
  inject,
  Output,
  ViewChild,
} from '@angular/core';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';
import { BusinessDetailFormComponent } from '../business-detail-form/business-detail-form.component';
import { BusinessAttributesFormComponent } from '../business-attributes-form/business-attributes-form.component';
import { BusinessLoanFormComponent } from '../business-loan-form/business-loan-form.component';
import { TechnicalAssistanceFormComponent } from '../technical-assistance-form/technical-assistance-form.component';
import { OperationalToolsFormComponent } from '../operational-tools-form/operational-tools-form.component';
import { RealEstateFormComponent } from '../real-estate-form/real-estate-form.component';
import { SolarEnergyFormComponent } from '../solar-energy-form/solar-energy-form.component';
import { FindingInvestorsFormComponent } from '../finding-investors-form/finding-investors-form.component';
import { Router } from '@angular/router';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-add-business-record',
  standalone: true,
  imports: [
    SecondaryPopupComponent,
    BusinessDetailFormComponent,
    BusinessAttributesFormComponent,
    BusinessLoanFormComponent,
    SvgIconComponent,
    TechnicalAssistanceFormComponent,
    OperationalToolsFormComponent,
    RealEstateFormComponent,
    SolarEnergyFormComponent,
    FindingInvestorsFormComponent,
  ],
  templateUrl: './add-business-record.component.html',
  styleUrl: './add-business-record.component.scss',
})
export class AddBusinessRecordComponent {
  router = inject(Router);
  auditLogService = inject(AuditLogService);
  private commonService = inject(CommonService);

  @Input() variant: 'default' | 'compact' = 'default';
  @Input() disableAddBusinessButton = false;
  @Input() buttonLabel = 'Add Business';
  @Input() fullWidth = true;

  @Output() addedBusiness = new EventEmitter<string>();

  @ViewChild('businessDetailForm')
  businessDetailForm?: BusinessDetailFormComponent;

  @ViewChild('businessAttributesForm')
  businessAttributesForm?: BusinessAttributesFormComponent;

  @ViewChild('businessLoanForm')
  businessLoanForm?: BusinessLoanFormComponent;

  @ViewChild('technicalAssistanceForm')
  technicalAssistanceForm?: TechnicalAssistanceFormComponent;

  @ViewChild('operationalToolsForm')
  operationalToolsForm?: OperationalToolsFormComponent;

  @ViewChild('realEstateForm')
  realEstateForm?: RealEstateFormComponent;

  @ViewChild('solarEnergyForm')
  solarEnergyForm?: SolarEnergyFormComponent;

  @ViewChild('findingInvestorsForm')
  findingInvestorsForm?: FindingInvestorsFormComponent;

  showAddBusinessPopup = false;
  showLoanForm = false;
  showTechnicalAssistanceForm = false;
  showOperationalToolsForm = false;
  showRealEstateForm = false;
  showSolarEnergyForm = false;
  showFindingInvestorsForm = false;
  showLeadAttributes = false;

  businessDetails: any = {};
  loanDetails: any = {};
  technicalAssistanceDetails: any = {};
  operationalToolsDetails: any = {};
  realEstateDetails: any = {};
  solarEnergyDetails: any = {};
  findingInvestorsDetails: any = {};
  attributeDetails: any = {};

  selectedSupportTypes: string[] = [];
  currentFormIndex = 0;

  private readonly FORM_ERROR_TOAST =
    'Please fix the highlighted errors before continuing.';

  private showFormErrorToast(): void {
    this.commonService.triggerToast({
      type: ToastType.Error,
      message: this.FORM_ERROR_TOAST,
    });
  }

  submitBusinessDetailForm(): void {
    this.businessDetailForm?.onSubmitForm();
    // After calling submit, check if validation failed
    if (this.businessDetailForm?.hasErrors) {
      this.showFormErrorToast();
    }
  }

  submitLoanForm(): void {
    this.businessLoanForm?.onSubmitForm();
    if (this.businessLoanForm?.hasErrors) {
      this.showFormErrorToast();
    }
  }

  submitTechnicalAssistanceForm(): void {
    this.technicalAssistanceForm?.onSubmitForm();
    if (this.technicalAssistanceForm?.hasErrors) {
      this.showFormErrorToast();
    }
  }

  submitOperationalToolsForm(): void {
    this.operationalToolsForm?.onSubmitForm();
    // Operational tools has no required fields — no toast needed
  }

  submitRealEstateForm(): void {
    this.realEstateForm?.onSubmitForm();
    if (this.realEstateForm?.hasErrors) {
      this.showFormErrorToast();
    }
  }

  submitSolarEnergyForm(): void {
    this.solarEnergyForm?.onSubmitForm();
    if (this.solarEnergyForm?.hasErrors) {
      this.showFormErrorToast();
    }
  }

  submitFindingInvestorsForm(): void {
    this.findingInvestorsForm?.onSubmitForm();
    if (this.findingInvestorsForm?.hasErrors) {
      this.showFormErrorToast();
    }
  }

  submitAttributesForm(): void {
    this.businessAttributesForm?.onSubmitForm();
  }

  onBack(): void {
    if (this.showLeadAttributes) {
      // Save current attributes form data
      if (this.businessAttributesForm) {
        this.attributeDetails =
          this.businessAttributesForm.leadAttributesForm.value;
      }
      this.showLeadAttributes = false;

      // Go back to the last form in the sequence
      if (this.selectedSupportTypes.length > 0) {
        this.currentFormIndex = this.selectedSupportTypes.length - 1;
        this.showCurrentForm();
      } else {
        this.showAddBusinessPopup = true;
      }
    } else {
      // Save current form data
      this.saveCurrentFormData();

      // Close current form
      this.closeAllSupportForms();

      // Go to previous form
      if (this.currentFormIndex > 0) {
        this.currentFormIndex--;
        this.showCurrentForm();
      } else {
        this.showAddBusinessPopup = true;
      }
    }
  }

  private saveCurrentFormData(): void {
    if (this.showLoanForm && this.businessLoanForm) {
      this.loanDetails = this.businessLoanForm.loanForm.value;
    } else if (
      this.showTechnicalAssistanceForm &&
      this.technicalAssistanceForm
    ) {
      this.technicalAssistanceDetails =
        this.technicalAssistanceForm.technicalAssistanceForm.value;
    } else if (this.showOperationalToolsForm && this.operationalToolsForm) {
      this.operationalToolsDetails =
        this.operationalToolsForm.operationalToolsForm.value;
    } else if (this.showRealEstateForm && this.realEstateForm) {
      this.realEstateDetails = this.realEstateForm.realEstateForm.value;
    } else if (this.showSolarEnergyForm && this.solarEnergyForm) {
      this.solarEnergyDetails = this.solarEnergyForm.solarEnergyForm.value;
    } else if (this.showFindingInvestorsForm && this.findingInvestorsForm) {
      this.findingInvestorsDetails =
        this.findingInvestorsForm.findingInvestorsForm.value;
    }
  }

  private closeAllSupportForms(): void {
    this.showLoanForm = false;
    this.showTechnicalAssistanceForm = false;
    this.showOperationalToolsForm = false;
    this.showRealEstateForm = false;
    this.showSolarEnergyForm = false;
    this.showFindingInvestorsForm = false;
  }

  private showCurrentForm(): void {
    this.closeAllSupportForms();

    if (this.currentFormIndex >= this.selectedSupportTypes.length) {
      return;
    }

    const currentType = this.selectedSupportTypes[this.currentFormIndex];

    switch (currentType) {
      case 'Loans and Capital Products':
        this.showLoanForm = true;
        break;
      case 'Small Business Technical Assistance':
        this.showTechnicalAssistanceForm = true;
        break;
      case 'Business Operational Tools':
        this.showOperationalToolsForm = true;
        break;
      case 'Commercial Real Estate':
        this.showRealEstateForm = true;
        break;
      case 'Solar and Energy Efficiency Products':
        this.showSolarEnergyForm = true;
        break;
      case 'Finding Investors':
        this.showFindingInvestorsForm = true;
        break;
    }
  }

  onNext(): void {
    this.showAddBusinessPopup = false;
    this.selectedSupportTypes = this.businessDetails.selectedSupportTypes || [];
    this.currentFormIndex = 0;

    if (this.selectedSupportTypes.length > 0) {
      this.showCurrentForm();
    } else {
      // If no support types selected (shouldn't happen with validation), go to attributes
      this.showLeadAttributes = true;
    }
  }

  onNextToAttributes(): void {
    this.currentFormIndex++;

    if (this.currentFormIndex < this.selectedSupportTypes.length) {
      // Show next form in sequence
      this.showCurrentForm();
    } else {
      // All forms completed, show attributes
      this.closeAllSupportForms();
      this.showLeadAttributes = true;
    }
  }

  onBusinessFormSubmit(formData: any) {
    this.businessDetails = formData;

    // Clear data for deselected support types
    const newSelectedTypes = formData.selectedSupportTypes || [];
    const supportTypeFormMap: Record<string, keyof AddBusinessRecordComponent> =
      {
        'Loans and Capital Products': 'loanDetails',
        'Small Business Technical Assistance': 'technicalAssistanceDetails',
        'Business Operational Tools': 'operationalToolsDetails',
        'Commercial Real Estate': 'realEstateDetails',
        'Solar and Energy Efficiency Products': 'solarEnergyDetails',
        'Finding Investors': 'findingInvestorsDetails',
      };

    // Clear details for support types that were deselected
    Object.entries(supportTypeFormMap).forEach(([supportType, detailsKey]) => {
      if (!newSelectedTypes.includes(supportType)) {
        (this as any)[detailsKey] = {};
      }
    });

    this.onNext();
  }

  onLoanFormSubmit(formData: any) {
    this.loanDetails = formData;
    this.onNextToAttributes();
  }

  onTechnicalAssistanceFormSubmit(formData: any) {
    this.technicalAssistanceDetails = formData;
    this.onNextToAttributes();
  }

  onOperationalToolsFormSubmit(formData: any) {
    this.operationalToolsDetails = formData;
    this.onNextToAttributes();
  }

  onRealEstateFormSubmit(formData: any) {
    this.realEstateDetails = formData;
    this.onNextToAttributes();
  }

  onEnergyUpgradesChange(interestedInEnergy: boolean) {
    const solarSupportType = 'Solar and Energy Efficiency Products';

    if (interestedInEnergy) {
      // Add Solar and Energy Efficiency Products if not already present
      if (!this.selectedSupportTypes.includes(solarSupportType)) {
        // Insert after Commercial Real Estate in the list
        const realEstateIndex = this.selectedSupportTypes.indexOf(
          'Commercial Real Estate',
        );
        if (realEstateIndex !== -1) {
          this.selectedSupportTypes.splice(
            realEstateIndex + 1,
            0,
            solarSupportType,
          );
        } else {
          this.selectedSupportTypes.push(solarSupportType);
        }

        // Update businessDetails as well
        if (this.businessDetails.selectedSupportTypes) {
          if (
            !this.businessDetails.selectedSupportTypes.includes(
              solarSupportType,
            )
          ) {
            this.businessDetails.selectedSupportTypes.push(solarSupportType);
          }
        }
      }
    } else {
      // Remove Solar and Energy Efficiency Products
      const index = this.selectedSupportTypes.indexOf(solarSupportType);
      if (index !== -1) {
        this.selectedSupportTypes.splice(index, 1);
      }

      // Clear solar energy details
      this.solarEnergyDetails = {};

      // Update businessDetails as well
      if (this.businessDetails.selectedSupportTypes) {
        const businessIndex =
          this.businessDetails.selectedSupportTypes.indexOf(solarSupportType);
        if (businessIndex !== -1) {
          this.businessDetails.selectedSupportTypes.splice(businessIndex, 1);
        }
      }
    }
  }

  isSolarAlreadySelected(): boolean {
    return this.selectedSupportTypes.includes(
      'Solar and Energy Efficiency Products',
    );
  }

  onSolarEnergyFormSubmit(formData: any) {
    this.solarEnergyDetails = formData;

    // If credit score range was answered in solar form, sync it to loanDetails
    if (formData.creditScoreRange && !this.loanDetails.creditScoreRange) {
      this.loanDetails.creditScoreRange = formData.creditScoreRange;
    }

    this.onNextToAttributes();
  }

  onFindingInvestorsFormSubmit(formData: any) {
    this.findingInvestorsDetails = formData;
    this.onNextToAttributes();
  }

  getCurrentFormTitle(): string {
    if (this.selectedSupportTypes.length <= 1) {
      return this.getFormTitleByType(
        this.selectedSupportTypes[this.currentFormIndex],
      );
    }

    const stepInfo = `Step ${this.currentFormIndex + 1} of ${this.selectedSupportTypes.length}`;
    const formTitle = this.getFormTitleByType(
      this.selectedSupportTypes[this.currentFormIndex],
    );
    return `${stepInfo}: ${formTitle}`;
  }

  private getFormTitleByType(type: string): string {
    switch (type) {
      case 'Loans and Capital Products':
        return 'Loans and Capital Products';
      case 'Small Business Technical Assistance':
        return 'Small Business Technical Assistance';
      case 'Business Operational Tools':
        return 'Business Operational Tools';
      case 'Commercial Real Estate':
        return 'Commercial Real Estate';
      case 'Solar and Energy Efficiency Products':
        return 'Solar and Energy Efficiency Products';
      case 'Finding Investors':
        return 'Finding Investors';
      default:
        return 'Business Support Information';
    }
  }

  closeAllPopups(isClickedOutside: boolean): void {
    if (!isClickedOutside) {
      this.businessDetails = {};
      this.loanDetails = {};
      this.technicalAssistanceDetails = {};
      this.operationalToolsDetails = {};
      this.realEstateDetails = {};
      this.solarEnergyDetails = {};
      this.findingInvestorsDetails = {};
      this.attributeDetails = {};
      this.selectedSupportTypes = [];
      this.currentFormIndex = 0;
      this.showAddBusinessPopup = false;
      this.showLoanForm = false;
      this.showTechnicalAssistanceForm = false;
      this.showOperationalToolsForm = false;
      this.showRealEstateForm = false;
      this.showSolarEnergyForm = false;
      this.showFindingInvestorsForm = false;
      this.showLeadAttributes = false;
    }
  }

  onAttributesFormSubmit(result: {
    success: boolean;
    message: string;
    formData?: any;
  }) {
    if (result.success) {
      this.auditLogService.logFeature({
        featureName: 'add_business_record',
        action: 'create',
        meta: {
          businessName: this.businessDetails?.company_name || '',
        },
        description: 'User submits a new business record',
      });

      this.businessDetails = {};
      this.loanDetails = {};
      this.technicalAssistanceDetails = {};
      this.operationalToolsDetails = {};
      this.realEstateDetails = {};
      this.solarEnergyDetails = {};
      this.findingInvestorsDetails = {};
      this.attributeDetails = {};
      this.selectedSupportTypes = [];
      this.currentFormIndex = 0;
      this.showLeadAttributes = false;
      this.addedBusiness.emit(result.message);
    } else {
      if (result.formData) {
        this.attributeDetails = result.formData;
      }
      this.commonService.triggerToast({
        type: ToastType.Error,
        message: result.message,
      });
    }
  }
}
